import { ref, onMounted, computed, inject, type ComputedRef } from 'vue'

import { loadCanvasAndDsrApps } from '@lib/composables/useInstalledApps'
import { isPitcherWkWebView } from '@lib/util/userAgents'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { AppFile, EmbeddedAppShortcut, EmbeddedAppShortcutsDict } from '@lib/types/app'
import useCanvas from '@canvas-builder/composables/useCanvas'
import {
  findAllEmbeddableTypesInCanvasContent,
  findAllEmbeddableTypesInSectionsContent,
} from '@canvas-builder/util/canvas.util'

export function useInstalledEmbeddables() {
  const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
    'launchDarkly',
    computed(() => ({})),
  )
  const { mode, componentNodesById, sectionComponentNodesBySectionId } = useCanvas()

  const installedEmbeddablesById = ref<Record<AppFile['id'], AppFile> | null>(null)
  const isFetching = ref(false)
  let refetchedFor: Record<string, boolean> = {}
  const shouldUseDeviceUrl = computed(() => launchDarkly.value?.prefer_online_handlers && isPitcherWkWebView)

  const shortcutLocations = ['fullscreen', 'presentation', 'edit'] satisfies (keyof EmbeddedAppShortcutsDict)[]
  const shortcuts = computed(() => {
    if (!installedEmbeddablesById.value || mode.value !== 'impact')
      return { fullscreen: [], presentation: [], edit: [] }

    const embeddablesInCanvasContent = findAllEmbeddableTypesInCanvasContent(componentNodesById.value)
    const embeddablesInSectionsContent = findAllEmbeddableTypesInSectionsContent(sectionComponentNodesBySectionId.value)
    const embeddablesInCanvas = new Set<string>([
      ...Array.from(embeddablesInCanvasContent.values()),
      ...Array.from(embeddablesInSectionsContent.values()),
    ])
    const installedEmbeddablesInCanvasById = computed(() => {
      if (!installedEmbeddablesById.value) return {}

      return Object.fromEntries(
        Object.entries(installedEmbeddablesById.value).filter(([, app]) =>
          embeddablesInCanvas.has(app.app_metadata?.name || ''),
        ),
      )
    })

    const result = Object.values(installedEmbeddablesInCanvasById.value).reduce(
      (acc, app) => {
        const moduleShortcuts = app.app_metadata?.module?.canvas?.shortcuts
        if (!moduleShortcuts) return acc
        shortcutLocations.forEach((type) => {
          moduleShortcuts[type]?.forEach((shortcut) => {
            if (app.app_metadata?.name) {
              const newShortcut = {
                ...shortcut,
                appName: app.app_metadata.name,
                displayName: app.app_metadata?.display_name || app.app_metadata?.displayName || app.app_metadata?.name,
              }
              const index = acc[type].findIndex(
                (s) => (s.order_override ?? Infinity) > (shortcut.order_override ?? Infinity),
              )
              if (index === -1) {
                acc[type].push(newShortcut)
              } else {
                acc[type].splice(index, 0, newShortcut)
              }
            }
          })
        })
        return acc
      },
      { fullscreen: [], presentation: [], edit: [] } as Record<
        'fullscreen' | 'presentation' | 'edit',
        (EmbeddedAppShortcut & { appName: string; displayName: string })[]
      >,
    )

    return result
  })

  const refetch = (fileId?: AppFile['id']) => {
    if ((!shouldUseDeviceUrl.value && fileId) || mode.value !== 'impact') return
    else if (fileId && refetchedFor[fileId]) return
    else if (fileId && !refetchedFor[fileId]) {
      refetchedFor[fileId] = true
    }

    isFetching.value = true
    loadCanvasAndDsrApps()
      .then((apps) => {
        const appsById = apps.reduce(
          (acc, app) => {
            acc[app.id] = app
            return acc
          },
          {} as Record<AppFile['id'], AppFile>,
        )
        installedEmbeddablesById.value = appsById
      })
      .finally(() => {
        isFetching.value = false
      })
  }

  const getDeviceUrl = (fileId?: AppFile['id']) => {
    if (!fileId) return undefined
    const app = installedEmbeddablesById.value?.[fileId]

    if (!app) {
      refetch(fileId)
      return undefined
    }
    return app.content_url || undefined
  }

  const cleanup = () => {
    refetchedFor = {}
    installedEmbeddablesById.value = null
  }

  onMounted(() => {
    refetch()
  })

  return {
    isFetching,
    installedEmbeddablesById,
    refetchedFor,
    shouldUseDeviceUrl,
    shortcuts,
    refetch,
    cleanup,
    getDeviceUrl,
  }
}
